import React, { useEffect } from 'react';
import { registerPresenter } from '../scanExportStore';
import useBackgroundRuns from './useBackgroundRuns';
import ExportedStack from './ExportedStack';
import './RenderBar.css';

// Shared bottom bar of Studio ("render") and Export ("export"): one component so the two tabs can't drift.
// Three states, all driven by the parent:
//   idle     → what's queued (thumbs + `status`) and the primary action
//   running  → `running` = { label, done, total } : label + progress bar
//   done     → `confirmation` node (✓ N … ) and the follow-up actions
// `thumbs` = [{ id, src, state: 'queued' | 'rendering' | 'ready' | 'error' }] — thumbnails only, no titles, no text:
// the colour strip carries the state (grey queued, orange rendering, green ready, red error).
// The bar is neutral while the user is editing; it only takes the orange tint (`rbar--active`) while a render/export
// is running or has just finished.
// `stack` = cards for the Studio's exported zone (see ExportedStack.js), shown instead of `thumbs`.
// `actions` = the right-hand buttons (use the rbar-back / rbar-go classes for the standard styles).
// `zone` pins the bar to the bottom of its own card, inside a scrolling column (Studio's editing zone); `fixed` pins it to the viewport.
// Export sits at the bottom of its own card.
// A Scan export / Export run in progress (they live in scanExportStore.js / exportRunStore.js, outside their tabs) is shown here whichever tab is open:
// as the running state when this bar is idle, appended to `sub` when this bar is itself busy, and as a confirmation
// (with a dismiss) when it finished while the user was elsewhere. `scanDock` = the global dock, which passes its own props.
export default function RenderBar({ thumbs, stack, status, note, running: hostRunning, confirmation: hostConfirmation, actions, fixed, zone, scanDock }) {
  const bg = useBackgroundRuns();
  useEffect(() => (scanDock ? undefined : registerPresenter()), [scanDock]);
  let running = hostRunning, confirmation = hostConfirmation;
  if (!scanDock) {
    if (bg.running) {
      if (hostRunning) running = Object.assign({}, hostRunning, { sub: [hostRunning.sub, bg.running.label].filter(Boolean).join(' · ') });
      else if (!hostConfirmation) running = bg.running;
    } else if (bg.finished && !hostRunning && !hostConfirmation) {
      confirmation = <>{bg.finished.text} <button type="button" className="rbar-dismiss" onClick={bg.finished.dismiss}>Dismiss</button></>;
    }
  }
  const active = !!(running || confirmation);
  const pct = running && running.total ? Math.round(running.done / running.total * 100) : 0;
  return (
    <div className={`tb-card-header rbar${fixed ? ' rbar--fixed' : ''}${scanDock ? ' rbar--dock' : ''}${zone ? ' rbar--zone' : ''}${active ? ' rbar--active' : ''}${stack ? ' rbar--stack' : ''}`}>
      {stack && <ExportedStack cards={stack.filter(Boolean)} />}
      {thumbs && thumbs.length > 0 && (
        <div className="rbar-thumbs">
          {thumbs.filter(Boolean).map(th => (
            <span key={th.id} className={`rbar-th${th.onClick ? ' rbar-th--click' : ''}`} title={th.title || undefined} onClick={th.onClick || undefined}>
              {th.src && <video src={th.src} muted playsInline preload="metadata" />}
              <span className={`rbar-chip rbar-chip--${th.state}`} title={th.state === 'error' ? 'failed' : th.state} />
            </span>
          ))}
        </div>
      )}
      {running ? (
        <>
          <span className="rbar-state"><b>{running.label}</b>{running.sub && <span className="rbar-eta">{running.sub}</span>}</span>
          <span className="rbar-prog"><i style={{ width: `${pct}%` }} /></span>
        </>
      ) : confirmation ? (
        <span className="rbar-state rbar-confirm">{confirmation}</span>
      ) : (
        <span className="rbar-state">{status}{note && <span className="rbar-note">{note}</span>}</span>
      )}
      <span className="rbar-actions">{actions}</span>
    </div>
  );
}
